import { toNumberTimestamp } from '../../../util.js';
import { tickStep } from '../../generators/scaleMath';

type Interval = { duration: number; unit: 'fixed' | 'week' | 'month' | 'year'; step: number };
const second = 1000, minute = 60000, hour = 3600000, day = 86400000;
const intervals: Interval[] = [
  ...[1, 5, 15, 30].map(step => ({ duration: second * step, unit: 'fixed' as const, step: second * step })),
  ...[1, 5, 15, 30].map(step => ({ duration: minute * step, unit: 'fixed' as const, step: minute * step })),
  ...[1, 3, 6, 12].map(step => ({ duration: hour * step, unit: 'fixed' as const, step: hour * step })),
  ...[1, 2].map(step => ({ duration: day * step, unit: 'fixed' as const, step: day * step })),
  { duration: 7 * day, unit: 'week', step: 1 },
  { duration: 30 * day, unit: 'month', step: 1 },
  { duration: 90 * day, unit: 'month', step: 3 },
  { duration: 365 * day, unit: 'year', step: 1 },
];

/** Target a readable tick count while preserving round UTC calendar boundaries. */
export const generateUtcTimeTicks = (domain: (string | number | Date)[], count = 5): Date[] => {
  if (!Number.isFinite(count) || count <= 0 || domain.length < 2) return [];
  const first = toNumberTimestamp(domain[0]), last = toNumberTimestamp(domain[domain.length - 1]);
  if (first === undefined || last === undefined) return [];
  const min = Math.min(first, last), max = Math.max(first, last);
  if (min === max) return [new Date(min)];
  const target = (max - min) / Math.max(1, Math.floor(count) - 1);
  let interval: Interval;
  if (target < second) {
    const step = Math.max(1, tickStep(0, max - min, Math.max(1, count - 1)));
    interval = { duration: step, unit: 'fixed', step };
  } else if (target > 365 * day) {
    const step = Math.max(1, tickStep(0, (max - min) / (365 * day), Math.max(1, count - 1)));
    interval = { duration: step * 365 * day, unit: 'year', step };
  } else {
    interval = intervals.reduce((best, candidate) =>
      Math.abs(Math.log(candidate.duration / target)) < Math.abs(Math.log(best.duration / target)) ? candidate : best);
  }

  const date = new Date(min);
  const { unit, step } = interval;
  if (unit === 'fixed') date.setTime(Math.floor(min / step) * step);
  if (unit === 'week') {
    date.setUTCHours(0, 0, 0, 0);
    date.setUTCDate(date.getUTCDate() - date.getUTCDay());
  }
  if (unit === 'month') {
    date.setUTCHours(0, 0, 0, 0);
    date.setUTCDate(1);
    date.setUTCMonth(Math.floor(date.getUTCMonth() / step) * step);
  }
  if (unit === 'year') {
    date.setUTCHours(0, 0, 0, 0);
    date.setUTCMonth(0, 1);
    date.setUTCFullYear(Math.floor(date.getUTCFullYear() / step) * step);
  }
  const advance = () => {
    if (unit === 'fixed') date.setTime(+date + step);
    if (unit === 'week') date.setUTCDate(date.getUTCDate() + 7 * step);
    if (unit === 'month') date.setUTCMonth(date.getUTCMonth() + step);
    if (unit === 'year') date.setUTCFullYear(date.getUTCFullYear() + step);
  };
  if (+date < min) advance();
  const ticks: Date[] = [];
  while (+date <= max) {
    ticks.push(new Date(date));
    const previous = +date;
    advance();
    if (!(+date > previous)) break;
  }
  return first > last ? ticks.reverse() : ticks;
};
